import { z } from "zod";

/**
 * Bloques V0 de la capa de presentación (artefactos, cast, campos de acusación,
 * contenido del Visor). CaseV0 fue reemplazado por CaseV1 (cdl/case-v1.ts,
 * F1-T01); estos bloques siguen vivos porque la CDL v1 los compone.
 */

/** Apps core del OS; los módulos (F1-T07) agregan las suyas ("mod.banco", …) */
export const CORE_APPS = ["briefing", "chirp", "mail", "archivo", "visor"] as const;
export const CORE_ARTIFACT_TYPES = [
  "post",
  "perfil",
  "mail",
  "ficha",
  "parte",
  "foto",
  "carta",
] as const;

export const ArtifactV0 = z.object({
  id: z.string().regex(/^A\d+$/),
  /** App core o id de módulo — el validador exige coherencia con requiredModules */
  app: z.string().min(1),
  type: z.string().min(1),
  /** CharacterId (C<n>) del autor diegético, si aplica */
  authorId: z
    .string()
    .regex(/^C\d+$/)
    .optional(),
  /** Timestamp diegético (el reloj del expediente, no el real) */
  ts: z.iso.datetime().optional(),
  /** Shape libre por `type` — el Factory de render decide (stack §6) */
  content: z.record(z.string(), z.unknown()),
  /** Ids de artefactos que se abren desde este (adjuntos, fotos) */
  attachments: z.array(z.string().regex(/^A\d+$/)).default([]),
});

/**
 * Capa de texto de un artefacto visual. El texto de un documento NUNCA se quema
 * en el pixel: vive acá, como dato, y la app lo dibuja sobre la imagen base.
 *
 * Por qué: (1) un modelo de imagen no escribe texto fiable y la pista del caso
 * depende de que se LEA; (2) texto quemado no se traduce (i18n, F1-T10) ni se
 * busca; (3) la base visual queda intercambiable sin reescribir el caso.
 * Es el prototipo del renderizador de artefactos de F2-T07.
 */
export const OverlayPanelV0 = z.object({
  /** Estilo tipográfico del panel (cada uno se ve como otro tipo de impreso) */
  template: z.enum(["etiqueta", "documento", "tabla", "tags", "manuscrito"]),
  /** Zona de la imagen que ocupa el panel, en % del lado (0-100) */
  frame: z.object({
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
    w: z.number().min(1).max(100),
    h: z.number().min(1).max(100),
  }),
  /** Inclinación en grados — un impreso real nunca está perfectamente recto */
  rotate: z.number().min(-20).max(20).default(0),
  /** Multiplicador del cuerpo tipográfico dentro del panel */
  scale: z.number().min(0.2).max(4).default(1),
  /**
   * Paso del pautado impreso en la base, en % del ANCHO de la imagen. Si está,
   * cada renglón del panel ocupa exactamente un paso y el texto cae entre las
   * líneas impresas en vez de encima. Sin esto, un renglón puede cruzar una
   * fila y leerse como tachada — una pista falsa que el jugador no merece.
   */
  pitch: z.number().min(0.5).max(25).optional(),
  /**
   * Ancho de cada columna en % del panel, para calcar la grilla impresa de la
   * base (un ledger ya trae sus columnas dibujadas). El sobrante se reparte
   * entre columnas, así el texto cae dentro de la celda y no sobre su línea.
   */
  columnsPct: z.array(z.number().min(1).max(100)).default([]),
  /** Dibuja una hoja de papel bajo el panel: un adjunto pegado sobre el documento */
  sheet: z.boolean().default(false),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  lines: z.array(z.string()).default([]),
  columns: z.array(z.string()).default([]),
  rows: z.array(z.array(z.string())).default([]),
  footer: z.string().optional(),
  /** template "tags": marcas sobre rostros/objetos, en % del panel */
  tags: z
    .array(
      z.object({
        x: z.number().min(0).max(100),
        y: z.number().min(0).max(100),
        name: z.string().min(1),
      }),
    )
    .default([]),
});

/** Contenido de un artefacto de la app Visor (lo valida `pnpm validate:case`) */
export const VisorContentV0 = z.object({
  src: z.string().min(1),
  caption: z.string().min(1),
  exif: z.string().min(1),
  overlays: z.array(OverlayPanelV0).default([]),
});

export const CharacterV0 = z.object({
  id: z.string().regex(/^C\d+$/),
  name: z.string().min(1),
  /** Id de retrato en la biblioteca (estetica §2), placeholder en F0 */
  portrait: z.string().min(1),
  occupation: z.string().min(1),
  bio: z.string(),
});

export const AccusationFieldV0 = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** De dónde sale el vocabulario del formulario de huecos (jugabilidad §7) */
  vocabulary: z.enum(["cast", "pinned", "motive-list"]),
  /** Para vocabulary="motive-list": lista propia del campo (cómo/móvil separados); si falta, usa accusation.motives */
  options: z.array(z.string()).optional(),
});

export type ArtifactV0Data = z.infer<typeof ArtifactV0>;
export type CharacterV0Data = z.infer<typeof CharacterV0>;
